// The form schema sent by the API ({sections: [{key, title, subtitle, personType, formKey,
// items: [...]}]}) drives every renderer. This module holds the pure helpers: defaults,
// normalisation of saved data, dot-path access, conditions and vee-validate rules.
import { PERSON_TYPES } from "./personTypes";

export function getPath(obj, path) {
  if (!obj || !path) return undefined;
  return String(path)
    .split(".")
    .reduce((current, part) => (current && typeof current === "object" ? current[part] : undefined), obj);
}

// Plain assignment: callers pass objects that already have the intermediate keys (see
// emptyFormFromSchema), so Vue reactivity is preserved. Missing intermediates are created.
export function setPath(obj, path, value) {
  const parts = String(path).split(".");
  let current = obj;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      current[part] = value;
      return;
    }
    if (!current[part] || typeof current[part] !== "object") {
      current[part] = {};
    }
    current = current[part];
  });
  return obj;
}

export function defaultValue(item) {
  if (!item) return "";
  if (item.kind === "Notice") return { acknowledged: false, name: "" };
  switch (item.builtinKey) {
    case "recategorisation":
      return { classification: "Professional", criteria: { transactions: false, portfolio: false, professional: false }, name: "" };
    case "clientType":
    case "personSignature":
      return null;
    default:
      break;
  }
  switch (item.inputType) {
    case "checkbox":
      return false;
    case "checkboxes":
      return [];
    case "radio":
    case "select":
    case "heading":
      return null;
    default:
      return "";
  }
}

export function personSections(schema) {
  return ((schema && schema.sections) || []).filter((section) => section.personType);
}

export function sectionByKey(schema, key) {
  return ((schema && schema.sections) || []).find((section) => section.key === key) || null;
}

// Form keys of every person type (schema first, local fallback), so persons always exist.
export function allPersonFormKeys(schema) {
  const keys = {};
  Object.keys(PERSON_TYPES).forEach((type) => {
    keys[PERSON_TYPES[type].formKey] = type;
  });
  personSections(schema).forEach((section) => {
    if (section.formKey) keys[section.formKey] = section.personType;
  });
  return keys;
}

export function emptyPerson(section) {
  const person = {};
  ((section && section.items) || []).forEach((item) => {
    if (item.inputType === "heading" || item.builtinKey === "personSignature") return;
    person[item.dataKey] = item.kind === "Notice" ? null : defaultValue(item);
  });
  if (!Object.prototype.hasOwnProperty.call(person, "name")) person.name = "";
  return person;
}

export function emptyFormFromSchema(schema) {
  const form = { persons: {} };
  Object.keys(allPersonFormKeys(schema)).forEach((formKey) => {
    form.persons[formKey] = [];
  });
  ((schema && schema.sections) || []).forEach((section) => {
    if (section.personType) return;
    section.items.forEach((item) => {
      if (item.inputType === "heading") return;
      setPath(form, item.dataKey, defaultValue(item));
    });
  });
  if (!form.sectionE) form.sectionE = { acknowledged: false, name: "" };
  if (!form.sectionA) form.sectionA = {};
  return form;
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

// saved wins; keys unknown to the schema are kept (nothing is dropped on the next save)
function merge(base, saved) {
  if (!isPlainObject(saved)) return saved === undefined ? base : saved;
  const out = isPlainObject(base) ? { ...base } : {};
  Object.keys(saved).forEach((key) => {
    out[key] = isPlainObject(saved[key]) && isPlainObject(out[key]) ? merge(out[key], saved[key]) : saved[key];
  });
  return out;
}

export function normaliseForm(saved, schema) {
  const base = emptyFormFromSchema(schema);
  const data = merge(base, saved || {});

  const sectionsByFormKey = {};
  personSections(schema).forEach((section) => {
    sectionsByFormKey[section.formKey] = section;
  });
  const persons = {};
  Object.keys(base.persons).forEach((formKey) => {
    const list = Array.isArray(data.persons && data.persons[formKey]) ? data.persons[formKey] : [];
    const template = emptyPerson(sectionsByFormKey[formKey] || null);
    persons[formKey] = list.map((person) => ({ ...template, ...(isPlainObject(person) ? person : {}) }));
  });
  // lists the schema does not know about are kept as they are
  Object.keys((data.persons && data.persons) || {}).forEach((formKey) => {
    if (!persons[formKey] && Array.isArray(data.persons[formKey])) persons[formKey] = data.persons[formKey];
  });
  data.persons = persons;
  return data;
}

/* ---------- conditions ---------- */

export function conditionApplies(condition, form, person) {
  if (!condition || !condition.when) return false;
  let value;
  if (person && condition.when.indexOf(".") === -1) {
    value = person[condition.when];
  } else {
    value = getPath(form, condition.when);
  }
  if (typeof value === "boolean") value = value ? "true" : "false";
  return (condition.in || []).indexOf(value === null || value === undefined ? null : String(value)) !== -1;
}

export function isHidden(item, form, person) {
  const condition = item && item.condition;
  return !!condition && condition.then === "hidden" && conditionApplies(condition, form, person);
}

export function effectiveRequired(item, form, person) {
  if (!item || !item.required) return false;
  const condition = item.condition;
  if (condition && conditionApplies(condition, form, person)) return false;
  return true;
}

// vee-validate rules for a question (string or object form)
export function rulesFor(item, form, person) {
  const required = effectiveRequired(item, form, person);
  if (item.inputType === "checkbox") {
    return required ? { required: { allowFalse: false } } : "";
  }
  const parts = [];
  if (required) parts.push("required");
  if (item.inputType === "email") parts.push("email");
  return parts.join("|");
}

export function isBlank(value) {
  if (value === null || value === undefined || value === false) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/* ---------- client-side mirror of the server's sign check ---------- */

// Returns null or {message, sectionKey}. noticeSignatures: {key: dataUrl|null} drawn now;
// existingNotices: {key: {name, signedAt}} from the payload.
export function validateForSign(schema, form, sectionKeys, noticeSignatures, existingNotices) {
  const sections = (schema && schema.sections) || [];
  for (let s = 0; s < sections.length; s++) {
    const section = sections[s];
    if (sectionKeys.indexOf(section.key) === -1) continue;
    const fail = (message) => ({ message, sectionKey: section.key });

    if (section.personType) {
      const entries = (form.persons && form.persons[section.formKey]) || [];
      const label = (PERSON_TYPES[section.personType] && PERSON_TYPES[section.personType].singular) || section.personType;
      for (let i = 0; i < entries.length; i++) {
        const person = entries[i];
        const who = person.name ? person.name : `${label} ${i + 1}`;
        for (let j = 0; j < section.items.length; j++) {
          const item = section.items[j];
          if (item.kind !== "Question" || item.inputType === "heading" || item.builtinKey === "personSignature") continue;
          if (item.builtinKey === "personName") {
            if (isBlank(person.name)) return fail(`Please enter the name of ${label.toLowerCase()} ${i + 1} in "${section.title}".`);
            continue;
          }
          if (isHidden(item, form, person) || !effectiveRequired(item, form, person)) continue;
          if (isBlank(person[item.dataKey])) return fail(`Please answer "${item.label}" for ${who} in "${section.title}".`);
        }
      }
      continue;
    }

    for (let j = 0; j < section.items.length; j++) {
      const item = section.items[j];
      if (item.kind === "Notice") {
        if (item.key === "DECLARATION") continue;
        const ack = getPath(form, item.dataKey) || {};
        if (item.notice && item.notice.ackMode === "Signature") {
          const existing = existingNotices && existingNotices[item.key];
          if (isBlank(ack.name) && !(existing && existing.name)) return fail(`Please enter the name of the signatory for "${item.label}".`);
          if (!(noticeSignatures && noticeSignatures[item.key]) && !existing) return fail(`Please sign "${item.label}".`);
        } else if (!ack.acknowledged) {
          return fail(`Please confirm that you have read and understood "${item.label}".`);
        }
        continue;
      }
      if (item.inputType === "heading") continue;
      if (item.builtinKey === "recategorisation") {
        const value = getPath(form, item.dataKey) || {};
        if (value.classification === "Retail") {
          const criteria = value.criteria || {};
          const ticked = [criteria.transactions, criteria.portfolio, criteria.professional].filter(Boolean).length;
          if (ticked > 0 && ticked < 2) return fail(`Please tick at least two reclassification criteria in "${section.title}", or none.`);
        }
        continue;
      }
      if (isHidden(item, form) || !effectiveRequired(item, form)) continue;
      const value = getPath(form, item.dataKey);
      if (item.inputType === "checkbox" ? !value : isBlank(value)) return fail(`Please answer "${item.label}" in "${section.title}".`);
    }
  }
  return null;
}
